import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Sparkles } from 'lucide-react';
import { siteInfo } from '../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Collection', path: '/collection', special: true },
    { name: 'Hub', path: '/hub' },
    { name: 'Webinar', path: '/webinar-registration' },
    { name: 'Chakras', path: '/chakras' },
    { name: 'Rx', path: '/healing-prescription' },
    { name: 'Root', path: '/root-for-your-team' },
    { name: 'Education', path: '/education' },
    { name: 'About', path: '/about' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top Bar */}
      <div className="bg-[#1e1919] py-2.5 px-4">
        <div className="flex items-center justify-center gap-8 text-sm">
          <span className="hidden sm:flex items-center gap-3">
            <span className="text-white font-bold tracking-wide">Crystal Rx <span className="text-[#FFB800]">$125</span></span>
            <span className="text-white/30">|</span>
            <span className="text-white font-bold tracking-wide">Frequency Lab <span className="animate-shimmer bg-[length:200%_100%] bg-gradient-to-r from-[#FFB800] via-[#FFEB3B] to-[#FFB800] bg-clip-text text-transparent">$44.44</span></span>
            <span className="text-white/30">|</span>
            <span className="text-white font-bold tracking-wide">Root Blessing <span className="text-[#FF6B9D]">$7.77</span></span>
          </span>
          <a href={`tel:${siteInfo.phone}`} className="text-[#FFB800] font-bold hover:text-[#FFEB3B] transition-colors tracking-wide">
            {siteInfo.phone}
          </a>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-500 border-b ${
          isScrolled
            ? 'bg-white/90 backdrop-blur-xl py-2 shadow-lg shadow-[#9D4EDD]/5 border-[#e8e4de]'
            : 'bg-[#faf8f5] py-3 border-transparent'
        }`}
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo">
            <div className="relative">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#9D4EDD] via-[#FF6B9D] to-[#FFB800] p-[2px] group-hover:shadow-[0_0_20px_rgba(157,78,221,0.3)] transition-shadow duration-300">
                <div className="w-full h-full rounded-[10px] bg-white flex items-center justify-center">
                  <span className="text-transparent bg-clip-text bg-gradient-to-br from-[#9D4EDD] to-[#FF6B9D] font-serif text-xl font-bold">G</span>
                </div>
              </div>
            </div>
            <div className="hidden sm:block">
              <h1 className="font-serif text-lg text-[#1e1919] tracking-wide leading-tight">
                Gemstone Minerals<span className="text-[#c9a96e] text-xs align-super ml-0.5">&reg;</span>
              </h1>
              <p className="text-[9px] uppercase tracking-[0.3em] text-[#9D4EDD] font-semibold">Crystal Healing</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              const active = isActive(link.path);
              if (link.special) {
                return (
                  <Link
                    key={link.name}
                    to={link.path}
                    className="relative mx-1 group"
                    data-testid={`nav-${link.name.toLowerCase()}`}
                  >
                    <div className="relative px-5 py-2 rounded-full overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-r from-[#9D4EDD] via-[#FF6B9D] to-[#FFB800] opacity-90 group-hover:opacity-100 transition-opacity" />
                      <div className="absolute inset-0 bg-gradient-to-r from-[#9D4EDD] via-[#FF6B9D] to-[#FFB800] blur-lg opacity-0 group-hover:opacity-40 transition-opacity" />
                      <span className="relative flex items-center gap-1.5 text-white text-sm font-semibold tracking-wide">
                        <Sparkles className="w-3.5 h-3.5" />
                        {link.name}
                      </span>
                    </div>
                  </Link>
                );
              }
              return (
                <Link
                  key={link.name}
                  to={link.path}
                  className="relative px-4 py-2 group"
                  data-testid={`nav-${link.name.toLowerCase()}`}
                >
                  <span className={`relative z-10 text-sm font-medium tracking-wide transition-colors duration-300 ${
                    active ? 'text-[#9D4EDD]' : 'text-[#4A4A6A] group-hover:text-[#1e1919]'
                  }`}>
                    {link.name}
                  </span>
                  {active && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#9D4EDD] shadow-[0_0_6px_2px_rgba(157,78,221,0.4)]" />
                  )}
                  <span className="absolute inset-0 rounded-lg bg-[#9D4EDD]/0 group-hover:bg-[#9D4EDD]/5 transition-colors duration-300" />
                </Link>
              );
            })}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-[#9D4EDD]/10 transition-colors"
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
          >
            <Menu className="w-6 h-6 text-[#4A4A6A]" />
          </button>
        </div>

        {/* Subtle bottom accent */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#9D4EDD]/20 to-transparent" />
      </header>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-50 lg:hidden transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
        <div
          className={`absolute right-0 top-0 h-full w-80 bg-white border-l border-[#e8e4de] shadow-2xl transform transition-transform duration-300 ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="p-6">
            <div className="flex justify-between items-center mb-10">
              <span className="font-serif text-xl text-[#1e1919]">Menu</span>
              <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 hover:bg-[#9D4EDD]/10 rounded-lg transition-colors">
                <X className="w-6 h-6 text-[#7A7A9A]" />
              </button>
            </div>
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-xl text-base font-medium tracking-wide transition-all ${
                    isActive(link.path)
                      ? 'bg-[#9D4EDD]/10 text-[#9D4EDD]'
                      : 'text-[#4A4A6A] hover:bg-[#faf8f5] hover:text-[#1e1919]'
                  } ${link.special ? 'bg-gradient-to-r from-[#9D4EDD]/10 to-[#FF6B9D]/10 border border-[#9D4EDD]/20 text-[#9D4EDD]' : ''}`}
                >
                  {link.special && <Sparkles className="w-4 h-4 inline mr-2 text-[#FFB800]" />}
                  {link.name}
                </Link>
              ))}
            </nav>
            <div className="mt-10 pt-10 border-t border-[#e8e4de]">
              <p className="text-xs text-[#7A7A9A] mb-2 uppercase tracking-wider">Contact</p>
              <a href={`tel:${siteInfo.phone}`} className="text-[#9D4EDD] font-medium">{siteInfo.phone}</a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
